import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { FileText, Download, Printer, Plus, Trash2, ChevronDown, Save, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { generateReportPDF } from '../lib/pdf';
import { useT } from '../contexts/LanguageContext';

// Available data sources for custom reports
const DATA_SOURCES = [
  { key: 'buildings', label: 'Buildings', endpoint: '/buildings', columns: ['name', 'address', 'floors', 'total_area_sqft'] },
  { key: 'assets', label: 'Assets', endpoint: '/assets', columns: ['name', 'asset_type', 'building_id', 'status', 'maintenance_frequency'] },
  { key: 'complaints', label: 'Complaints', endpoint: '/complaints', columns: ['title', 'category', 'priority', 'status', 'building_id', 'created_at'] },
  { key: 'ppm-tasks', label: 'PPM Tasks', endpoint: '/ppm-tasks', columns: ['title', 'asset_id', 'frequency_days', 'next_due_date', 'status'] },
  { key: 'staff', label: 'Staff', endpoint: '/staff', columns: ['name', 'role', 'department', 'phone', 'email'] },
  { key: 'suppliers', label: 'Suppliers', endpoint: '/suppliers', columns: ['name', 'category', 'contact_name', 'phone', 'email'] },
  { key: 'stock-items', label: 'Stock', endpoint: '/stock-items', columns: ['name', 'category', 'quantity', 'unit_price', 'min_quantity'] },
  { key: 'tenants', label: 'Tenants', endpoint: '/tenants', columns: ['name', 'unit_number', 'building_id', 'phone', 'email'] },
  { key: 'certificates', label: 'Certificates', endpoint: '/certificates', columns: ['title', 'category', 'issuer', 'expiry_date', 'status'] },
  { key: 'service-reports', label: 'Service Reports', endpoint: '/service-reports', columns: ['report_type', 'building_id', 'summary', 'hours_spent', 'created_at'] },
  { key: 'attendance', label: 'Attendance', endpoint: '/attendance', columns: ['staff_id', 'date', 'hours_worked', 'overtime_hours'] },
  { key: 'work-schedules', label: 'Work Schedules', endpoint: '/work-schedules', columns: ['title', 'work_type', 'frequency', 'next_due', 'status'] },
];

export default function CustomReports() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [savedReports, setSavedReports] = useState([]);
  const [company, setCompany] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [draft, setDraft] = useState({
    name: '',
    description: '',
    source: 'buildings',
    columns: ['name', 'address', 'floors'],
    filter_field: '',
    filter_value: '',
    sort_by: '',
    limit: 200,
  });

  useEffect(() => {
    fetchCompany();
    fetchSavedReports();
  }, []);

  const fetchCompany = async () => {
    try {
      const r = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompany(r.data.find(c => c.id === user?.company_id) || r.data[0]);
    } catch { /* ignore */ }
  };

  const fetchSavedReports = () => {
    try {
      const stored = localStorage.getItem('fmflexi_custom_reports');
      setSavedReports(stored ? JSON.parse(stored) : []);
    } catch { setSavedReports([]); }
  };

  const persistSavedReports = (list) => {
    localStorage.setItem('fmflexi_custom_reports', JSON.stringify(list));
    setSavedReports(list);
  };

  const handleSourceChange = (key) => {
    const src = DATA_SOURCES.find(s => s.key === key);
    setDraft({ ...draft, source: key, columns: src?.columns?.slice(0, 4) || [] });
  };

  const toggleColumn = (col) => {
    const isOn = draft.columns.includes(col);
    setDraft({
      ...draft,
      columns: isOn ? draft.columns.filter(c => c !== col) : [...draft.columns, col]
    });
  };

  const saveReport = () => {
    if (!draft.name.trim()) { toast.error('Report name required'); return; }
    const id = editingId || `r_${Date.now()}`;
    const next = editingId
      ? savedReports.map(r => r.id === editingId ? { ...draft, id } : r)
      : [...savedReports, { ...draft, id }];
    persistSavedReports(next);
    toast.success(editingId ? 'Report updated' : 'Report saved');
    setShowBuilder(false);
    setEditingId(null);
    setDraft({ name: '', description: '', source: 'buildings', columns: ['name', 'address', 'floors'], filter_field: '', filter_value: '', sort_by: '', limit: 200 });
  };

  const deleteReport = (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    persistSavedReports(savedReports.filter(r => r.id !== id));
  };

  const editReport = (r) => {
    setDraft({ ...r });
    setEditingId(r.id);
    setShowBuilder(true);
  };

  const runReport = async (report, mode = 'download') => {
    const src = DATA_SOURCES.find(s => s.key === report.source);
    if (!src) { toast.error('Invalid data source'); return; }
    try {
      const r = await axios.get(`${API}${src.endpoint}`, { withCredentials: true });
      let data = r.data || [];
      if (report.filter_field && report.filter_value) {
        data = data.filter(row =>
          String(row[report.filter_field] ?? '').toLowerCase().includes(String(report.filter_value).toLowerCase())
        );
      }
      if (report.sort_by) {
        data = [...data].sort((a, b) => String(a[report.sort_by] ?? '').localeCompare(String(b[report.sort_by] ?? '')));
      }
      if (report.limit) data = data.slice(0, parseInt(report.limit, 10));
      const cols = report.columns.length ? report.columns : src.columns;
      const head = cols.map(c => c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      const rows = data.map(row => cols.map(c => {
        const v = row[c];
        if (v == null) return '-';
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.split('T')[0];
        return String(v);
      }));
      generateReportPDF({
        title: report.name,
        subtitle: report.description || `${src.label} report`,
        company,
        sections: [
          {
            heading: 'Summary',
            head: ['Metric', 'Value'],
            rows: [
              ['Records returned', String(rows.length)],
              ['Source', src.label],
              ...(report.filter_field ? [[`Filter: ${report.filter_field}`, String(report.filter_value || '')]] : []),
              ...(report.sort_by ? [['Sorted by', String(report.sort_by)]] : []),
            ]
          },
          {
            heading: src.label,
            head,
            rows
          }
        ],
        print: mode === 'print'
      });
      toast.success(mode === 'print' ? 'Print dialog opened' : 'Report PDF generated');
    } catch (e) {
      toast.error('Failed to generate report');
    }
  };

  const currentSource = DATA_SOURCES.find(s => s.key === draft.source);

  return (
    <Layout>
      <div data-testid="custom-reports-page" className="space-y-6 max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <FileText className="w-6 h-6 text-teal-600" />{t('creport.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('creport.subtitle')}</p>
          </div>
          <Dialog open={showBuilder} onOpenChange={(o) => { setShowBuilder(o); if (!o) { setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-report-btn" className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-1" />New Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Report' : 'Build a Custom Report'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Report Name</Label>
                    <Input data-testid="report-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Monthly Asset Audit" />
                  </div>
                  <div>
                    <Label>Data Source</Label>
                    <Select value={draft.source} onValueChange={handleSourceChange}>
                      <SelectTrigger data-testid="report-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DATA_SOURCES.map(s => (
                          <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (subtitle)</Label>
                  <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Optional context for this report" />
                </div>

                <div>
                  <Label>Columns to Include</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {currentSource?.columns.map(col => (
                      <label key={col} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-teal-400 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          data-testid={`column-${col}`}
                          checked={draft.columns.includes(col)}
                          onChange={() => toggleColumn(col)}
                          className="rounded text-teal-600"
                        />
                        <span className="text-slate-700">{col.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Filter Field (optional)</Label>
                    <Select value={draft.filter_field || 'none'} onValueChange={(v) => setDraft({ ...draft, filter_field: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {currentSource?.columns.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Filter Value</Label>
                    <Input value={draft.filter_value} onChange={(e) => setDraft({ ...draft, filter_value: e.target.value })} placeholder="contains..." disabled={!draft.filter_field} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Sort By</Label>
                    <Select value={draft.sort_by || 'none'} onValueChange={(v) => setDraft({ ...draft, sort_by: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {currentSource?.columns.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Row Limit</Label>
                    <Input type="number" value={draft.limit} onChange={(e) => setDraft({ ...draft, limit: e.target.value })} />
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2 mt-4">
                <Button variant="outline" onClick={() => runReport(draft, 'download')} disabled={!draft.columns.length}>
                  <Download className="w-4 h-4 mr-1" />Preview PDF
                </Button>
                <Button data-testid="save-report-btn" onClick={saveReport} className="bg-teal-600 hover:bg-teal-700">
                  <Save className="w-4 h-4 mr-1" />{editingId ? 'Update' : 'Save'} Report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Saved reports list */}
        <Card className="overflow-hidden">
          {savedReports.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No saved reports yet</p>
              <p className="text-sm text-muted-foreground mt-1">Click "New Report" to build your first custom branded report.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {savedReports.map(r => {
                const src = DATA_SOURCES.find(s => s.key === r.source);
                return (
                  <div key={r.id} data-testid={`saved-report-${r.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-slate-50">
                    <div className="flex-1">
                      <div className="font-semibold text-foreground">{r.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {src?.label} · {r.columns.length} columns
                        {r.filter_field && ` · filter: ${r.filter_field}=${r.filter_value}`}
                      </div>
                      {r.description && <div className="text-xs text-muted-foreground mt-1 italic">{r.description}</div>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button data-testid={`run-pdf-${r.id}`} onClick={() => runReport(r, 'download')} size="sm" className="bg-teal-600 hover:bg-teal-700">
                        <Download className="w-3.5 h-3.5 mr-1" />PDF
                      </Button>
                      <Button data-testid={`run-print-${r.id}`} onClick={() => runReport(r, 'print')} size="sm" variant="outline">
                        <Printer className="w-3.5 h-3.5 mr-1" />Print
                      </Button>
                      <Button data-testid={`edit-report-${r.id}`} onClick={() => editReport(r)} size="sm" variant="ghost">
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-report-${r.id}`} onClick={() => deleteReport(r.id)} size="sm" variant="ghost" className="text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Quick info */}
        <Card className="p-4 bg-teal-50/50 border-teal-200">
          <div className="flex items-start gap-3">
            <ChevronDown className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5 rotate-[-90deg]" />
            <div className="text-sm text-teal-900">
              <strong>How it works:</strong> Pick a data source, choose which columns to include, add an optional filter or sort, and save the template. Every download produces a PDF with your company letterhead (logo + name + address + contact details from Settings). Use Print for a one-click browser print dialog. All saved reports are CRUD — edit or delete any time.
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
